import Configs from "../config";
import { ICategoryType } from "../generated/Api";
import { ITag, ITagUsedIn } from "../types/TagTypes";
import { DefaultResponse } from "../types/UtilTypes";
import { localStorageGetItem } from "../utils/LocalStorage";
import { CategoriesAPI } from "./Categories";
import queryString from "query-string";
export interface ICreateTag {
  name: string;
  category: string;
}

export interface IFindAllTagsFromUser {
  userUid: string;
}

export interface IDeleteManyTagsResponse {
  deletedTags: string[];
  cantBeDeleted: string[];
}

export interface ICampaignsUsedInTags {
  tagId: string;
  campaignsUsedIn: Array<{
    id: string;
    name: string;
    type: ITagUsedIn;
  }>;
}

const createFromImport = async (
  tagName: string,
  categoryId: string
): Promise<ITag> => {
  const userUid = localStorageGetItem("USER_UID");
  const token = localStorageGetItem("USER_TOKEN");

  const response = await fetch(`${Configs.SERVER_URL}/tag`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({
      userUid,
      name: tagName.replace(/\s\s+/g, " ").trim(),
      categoryId: categoryId,
    }),
  });

  const tag: DefaultResponse<ITag> = await response.json();
  if (tag.status === "ok") {
    return tag.data as ITag;
  }

  throw new Error(tag.code);
};

const create = async (
  body: ICreateTag,
  isSystemTag?: boolean
): Promise<ITag> => {
  const userUid = localStorageGetItem("USER_UID");
  const token = localStorageGetItem("USER_TOKEN");

  // All this is to support creating tags from our current things
  let allCategories = await CategoriesAPI.readAll();

  let categoryOfThisTag = allCategories.find(
    (c) => c.name.trim().toLowerCase() === body.category.trim().toLowerCase()
  );

  if (!categoryOfThisTag) {
    categoryOfThisTag = await CategoriesAPI.create({
      type: isSystemTag ? ICategoryType.System : ICategoryType.User,
      name: body.category.replace(/\s\s+/g, " ").trim(),
    });
  }

  const response = await fetch(`${Configs.SERVER_URL}/tag`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({
      userUid,
      name: body.name.replace(/\s\s+/g, " ").trim(),
      categoryId: categoryOfThisTag._id,
    }),
  });

  const tag: DefaultResponse<ITag> = await response.json();
  if (tag.status === "ok") {
    return tag.data as ITag;
  }

  throw new Error(tag.code);
};

const update = async (body: ITag): Promise<ITag> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/tag`, {
    method: "PATCH",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify(body),
  });

  const tag: DefaultResponse<ITag> = await response.json();
  if (tag.status === "ok") {
    return tag.data as ITag;
  }

  throw new Error(tag.code);
};

const readAll = async (params: {
  getCount?: boolean;
  getCampaigns?: boolean;
}): Promise<ITag[]> => {
  const token = localStorageGetItem("USER_TOKEN");

  const response = await fetch(
    `${Configs.SERVER_URL}/tag?${queryString.stringify(params)}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
        Authorization: "Bearer " + token,
      },
    }
  );

  const tags: DefaultResponse<ITag[]> = await response.json();
  if (tags.status === "ok") {
    return tags.data as ITag[];
  }

  throw new Error(tags.code);
};

const getCampaignsUsedInTags = async (
  tagIds: string[]
): Promise<ICampaignsUsedInTags[]> => {
  const token = localStorageGetItem("USER_TOKEN");

  const response = await fetch(
    `${Configs.SERVER_URL}/tag/campaignsusedintags`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify({
        tagIds,
      }),
    }
  );

  const campaignsUsedInTags: DefaultResponse<ICampaignsUsedInTags[]> =
    await response.json();
  if (campaignsUsedInTags.status === "ok") {
    return campaignsUsedInTags.data as ICampaignsUsedInTags[];
  }

  throw new Error(campaignsUsedInTags.code);
};

const remove = async (tagId: string): Promise<ITag> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/tag/${tagId}`, {
    method: "DELETE",
    headers: {
      Authorization: "Bearer " + token,
    },
  });

  const tag: DefaultResponse<ITag> = await response.json();
  if (tag.status === "ok") {
    return tag.data as ITag;
  } else if (tag.code === "TAG_USED_BY_CAMPAIGN") {
    return tag as any;
  } else {
    throw new Error(tag.code);
  }
};

const removeMany = async (
  tagIds: string[]
): Promise<IDeleteManyTagsResponse> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/tag/delete_many`, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ tagIds }),
  });

  const tag: DefaultResponse<IDeleteManyTagsResponse> = await response.json();
  if (tag.status === "ok") {
    return tag.data as IDeleteManyTagsResponse;
  } else {
    throw new Error(tag.code);
  }
};

export const TagApi = {
  create,
  createFromImport,
  update,
  readAll,
  getCampaignsUsedInTags,
  remove,
  removeMany,
};
